/**
 * Модуль для загрузки/ подгрузки данных с сервера
 */

import {config} from './config.js';

const DEFAULT_TIMEOUT_MS = 15000;

export const load = {
    scload: new Map(), // массив статусов загруженных скриптов и стилей
    waitclass: "wave-button",

    /**
     * POST запрос к сайту
     * @param {string} url Адрес запроса
     * @param {object} options  данные
     *      options.params - для передачи методом POST
     *      options.callback_param - для передачи в callback функцию
     * @param callback callback функция
     * @param {string} type тип запроса json/text
     * @param button кнопка
     * @private
     */
    request: async function(url, options = {}, callback, type = "json", button = null) {
        const ctrl = button ? createSmartButtonControl(button) : null;
        const { params = null, callback_param = null, signal = undefined, timeoutMs = DEFAULT_TIMEOUT_MS, useXHR = false, onUploadProgress = null, onDownloadProgress = null } = options;

        try {
            const finalUrl = addCacheBuster(url);

            // Подготовим заголовки/тело
            const headers = new Headers();
            let body = null;

            if (params instanceof FormData) {
                body = params; // boundary проставит браузер
            } else if (params instanceof URLSearchParams) {
                body = params;
                headers.set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
            } else if (params && typeof params === 'object') {
                body = JSON.stringify(params);
                headers.set('Content-Type', 'application/json;charset=UTF-8');
            } else {
                body = params ?? null;
            }

            // Если есть кнопка — показываем загрузку:
            if (ctrl) ctrl.startLoading( !useXHR ); // при fetch upload-прогресса нет => индетерминированно

            // ВЕТКА 1: XHR для upload-прогресса
            if (useXHR) {
                const headersObj = {};
                headers.forEach((v,k) => { headersObj[k] = v; });

                const { signal: s, cancel } = withTimeout(signal, timeoutMs);

                try {
                    const res = await xhrPostWithProgress(
                        finalUrl,
                        { headers: headersObj, body, timeoutMs, signal: s },
                        // onUpload:
                        (percent) => {
                            if (percent === null) { ctrl && ctrl.startLoading(true); }
                            else { ctrl && ctrl.setProgress(percent); }
                            if (typeof onUploadProgress === 'function') onUploadProgress(percent);
                        },
                        // onDownload:
                        (percent) => {
                            if (typeof onDownloadProgress === 'function') onDownloadProgress(percent);
                        }
                    );
                    cancel();

                    // redirect
                    if (res.responseURL && res.responseURL !== finalUrl) {
                        window.location.href = res.responseURL;
                        return true;
                    }

                    // распарсить по типу/заголовкам — ДЕЛАЕМ ВСЕГДА, даже при не-2xx
                    const ct = res.headersObj['content-type'] || res.headersObj['content-type'];
                    let data;
                    if (type === 'json' || (ct && ct.includes('application/json'))) {
                        try { data = JSON.parse(res.bodyText || 'null'); } catch { data = null; }
                    } else {
                        data = res.bodyText;
                    }

                    const ok = res.status >= 200 && res.status < 300;

                    // пробрасываем статус и ok в data, если это объект JSON
                    if (data && typeof data === 'object') {
                        data.__httpStatus = res.status;
                        if (typeof data.ok === 'undefined') data.ok = ok;
                    }

                    if (typeof callback === 'function') callback(data, callback_param);

                    if (ok) {
                        ctrl && ctrl.success();
                    } else {
                        console.log(`[load] Ошибка HTTP: ${res.status}`, url, data);
                        ctrl && ctrl.error();
                    }
                    // Возвращаем data даже при ошибке, чтобы верхний уровень мог показать её
                    return data;

                } catch (e) {
                    console.log('[load] Сетевая ошибка (XHR):', e, url);
                    ctrl && ctrl.error();
                    return false;
                } finally {
                    ctrl && ctrl.reset(1200);
                }
            }

            // ВЕТКА 2: fetch (индетерминированная волна пока нет ответа; после ответа — можно показать download-прогресс, но обычно ответ мал)
            const { signal: s, cancel } = withTimeout(signal, timeoutMs);
            const response = await fetch(finalUrl, {
                method: 'POST',
                cache: 'no-store',
                headers,
                body,
                signal: s,
                redirect: 'follow'
            }).finally(cancel);

            if (response.redirected) {
                window.location.href = response.url;
                return true;
            }

            // Парсим тело ВСЕГДА, чтобы получить JSON-ошибку от сервера
            let data;
            try {
                const totalHeader = response.headers.get('Content-Length') || response.headers.get('content-length');
                if (totalHeader && response.body) {
                    let received = 0;
                    const total = parseInt(totalHeader, 10);
                    const reader = response.body.getReader();
                    const chunks = [];
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        chunks.push(value);
                        received += value.byteLength ?? value.length ?? 0;
                        const percent = Math.round((received / total) * 100);
                        ctrl && ctrl.setProgress(percent);
                    }
                    const merged = new Blob(chunks);
                    const res2 = new Response(merged, { status: response.status, statusText: response.statusText, headers: response.headers });
                    data = await parseByContentType(res2, type);
                } else {
                    data = await parseByContentType(response, type);
                }
            } catch {
                data = await parseByContentType(response, type).catch(() => null);
            }

            const ok = response.ok;

            // пробрасываем статус и ok в JSON-ответ
            if (data && typeof data === 'object') {
                data.__httpStatus = response.status;
                if (typeof data.ok === 'undefined') data.ok = ok;
            }

            if (typeof callback === 'function') callback(data, callback_param);

            if (ok) {
                ctrl && ctrl.success();
            } else {
                console.log(`[load] Ошибка HTTP: ${response.status}`, url, data);
                ctrl && ctrl.error();
            }
            return data;

        } catch (e) {
            console.log('[load] Сетевая ошибка:', e, url);
            ctrl && ctrl.error();
            return false;
        } finally {
            ctrl && ctrl.reset(1200);
        }
    },

    /**
     * GET запрос
     * @param url
     * @param callback callback функция
     * @param type тип запроса json/text
     * @param button
     * @param options
     * @returns {Promise<boolean|*>}
     */
    get: async function(url, callback, type = "json", button = null, options = {}) {
        const $btn = normalizeButton(button);
        const { signal = undefined, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

        // если кнопка не передана — ведём себя по-старому
        if (!button) {
            try {
                const finalUrl = addCacheBuster(url);
                const { signal: s, cancel } = withTimeout(signal, timeoutMs);
                const response = await fetch(finalUrl, { cache: 'no-store', signal: s }).finally(cancel);

                if (response.redirected) { window.location.href = response.url; return true; }
                if (!response.ok) { console.log(`[load] Ошибка HTTP: ${response.status}`, url); return false; }

                const data = await parseByContentType(response, type);
                if (typeof callback === 'function') callback(data);
                return data;
            } catch (e) {
                console.log('[load] Сетевая ошибка:', e, url);
                return false;
            }
        }

        // если кнопка передана — прогресс-волна
        const ctrl = createSmartButtonControl(button);
        const finalUrl = addCacheBuster(url);
        const { signal: s, cancel } = withTimeout(signal, timeoutMs);

        try {
            ctrl.startLoading(/* indeterminate = */ false); // попробуем показать реальный прогресс

            const { response, getBody } = await fetchWithDownloadProgress(finalUrl, { cache: 'no-store', signal: s }, (percent) => {
                if (percent === null) {
                    // total неизвестен
                    ctrl.startLoading(true); // индетерминированно
                } else {
                    ctrl.setProgress(percent);
                }
            });
            cancel();

            if (response.redirected) { window.location.href = response.url; return true; }
            if (!response.ok) { console.log(`[load] Ошибка HTTP: ${response.status}`, url); ctrl.error(); return false; }

            const data = await (type ? (type === 'json' ? response.json() : response.text()) : getBody());
            ctrl.success();
            if (typeof callback === 'function') callback(data);
            return data;
        } catch (e) {
            console.log('[load] Сетевая ошибка:', e, url);
            ctrl && ctrl.error();
            return false;
        } finally {
            ctrl && ctrl.reset(1200);
        }
    },

    /**
     * Перезагрузка блока с переданным id
     * у блока должен быть атрибут data-reload-url с урлом, по которому запрашивать данные
     * переданный контент должен находиться в контейнере #content-ajax
     * @param id_block
     * @param type
     * @returns {boolean}
     */
    reloadblock: function( id_block, type = 'replace' ) {
        const $block = $(id_block);
        if (!$block.length) return false;

        const url = $block.data('reload-url');
        if (!url) return false;

        const param = { container: id_block, type }; // 'insert'|'replace'
        return load.request(url, { callback_param: param }, load.__setcontent, 'text');
    },

    /**
     * подгрузка скрипта
     * @param src
     * @param id
     * @returns {Promise<unknown>}
     */
    script: function (src, id = null) {
        if (load.scload.has(src)) { return load.scload.get(src).promise; }

        const promise = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            if (id) { s.id = id; }
            s.defer = true; // безопаснее для исполнения
            s.onload = () => { config.debug && console.log("[load]", src); load.scload.set(src, { status: 'load', promise }); resolve('Done'); };
            s.onerror = () => { load.scload.set(src, { status: 'error', promise }); reject(new Error(`Ошибка загрузки скрипта ${src}`)); };
            s.src = src;
            document.head.appendChild(s);
        });

        load.scload.set(src, { status: 'loading', promise });

        return promise;
    },

    /**
     * подключение CSS файла
     * @param href
     * @param toEnd
     * @returns {Promise<unknown>}
     */
    css: function(href, toEnd = false) {
        if (load.scload.has(href)) return load.scload.get(href).promise;

        const promise = new Promise((resolve, reject) => {
            const l = document.createElement('link');
            l.rel = 'stylesheet';
            l.href = href;
            l.type = 'text/css';
            l.media = 'all';
            l.onload = () => { config.debug && console.log("[load]", href); load.scload.set(href, { status: 'load', promise }); resolve('Done'); };
            l.onerror = () => { load.scload.set(href, { status: 'error', promise }); reject(new Error(`Ошибка загрузки стилей ${href}`)); };

            (toEnd ? document.body : document.head).appendChild(l);
        });

        load.scload.set(href, { status: 'loading', promise });
        return promise;
    },

    /**
     * вставка полученного контента в блок
     * @param result
     * @param param
     */
    __setcontent: function(result, param) {
        const _data = $('<div>').html(result); // вместо append(result)
        const content = _data.find('#content-ajax').html() || '';

        if (param && param.container) {
            if (param.type === 'insert') {
                $(param.container).html(content);
            } else {
                $(param.container).replaceWith(content);
            }
        }
    },

    /**
     * временная метка
     * @returns {number}
     */
    time: function() {
        return Date.now();
    }
}

/**
 * допускаем jQuery или null/undefined
 * @param btn
 * @returns {null|*|jQuery|HTMLElement}
 */
function normalizeButton(btn) {
    // допускаем jQuery или null/undefined
    if (!btn) return null;
    return (typeof btn.addClass === 'function') ? btn : $(btn);
}

/**
 * кеш-бастер
 * @param rawUrl
 * @returns {string}
 */
function addCacheBuster(rawUrl) {
    const u = new URL(rawUrl, window.location.origin);
    u.searchParams.set('tm', String(Date.now()));
    return u.toString();
}

/**
 *
 * @param signal
 * @param ms
 * @returns {{cancel: (function(): void), signal: AbortSignal}}
 */
function withTimeout(signal, ms) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort('timeout'), ms);
    const composite = signal ? new AbortController() : ctrl;
    if (signal) {
        signal.addEventListener('abort', () => composite.abort('upstream abort'));
    }
    return { signal: composite.signal, cancel: () => clearTimeout(timer) };
}

/**
 * Разбор переданного контента
 * @param response
 * @param forcedType
 * @returns {Promise<*>}
 */
async function parseByContentType(response, forcedType) {
    if (forcedType === 'text') return response.text();
    if (forcedType === 'json') return response.json();

    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) return response.json();
    return response.text();
}

/**
 * enhanceButton(btn)
 * превращает кнопку <button> в "умную" кнопку с волной, если она ещё не преобразована
 * @param btn
 * @returns {*}
 */
function enhanceButton(btn) {
    if (!btn) return;

    load.css(config.template + 'js/vendor/enhance-button/enhance-button.css');

    if (btn.classList.contains('req-btn')) return btn; // already
    const text = btn.textContent.trim() || btn.getAttribute('aria-label') || 'Действие';
    btn.innerHTML = `<span class="req-btn__label">${text}</span><span class="req-btn__wave" aria-hidden="true"></span>`;
    btn.classList.add('req-btn');
    btn.setAttribute('type', btn.getAttribute('type') || 'button');
    btn.setAttribute('aria-live', 'polite');
    btn.setAttribute('aria-busy', 'false');
    return btn;
}

/**
 * normalizeTargets(target)
 *  - target: selector string | DOM Node | NodeList | jQuery object | Array
 *  - возвращает массив DOM-элементов
 */
function normalizeTargets(target) {
    // если jQuery объект
    if (typeof window.jQuery !== 'undefined' && target && (target instanceof window.jQuery || (Array.isArray(target) && target[0] instanceof window.jQuery))) {
        const arr = [];
        target.each ? target.each((i, el) => arr.push(el)) : target.forEach(el => arr.push(el));
        return arr;
    }

    // если селектор строкой
    if (typeof target === 'string') {
        return Array.from(document.querySelectorAll(target));
    }

    // если DOM node
    if (target instanceof Element) return [target];

    // если NodeList или Array of nodes
    if (NodeList.prototype.isPrototypeOf(target) || Array.isArray(target)) {
        return Array.from(target).filter(el => el instanceof Element);
    }

    // иначе — пустой массив
    return [];
}

/**
 * Превращает DOM-кнопку в «умную» с волной (если ещё не).
 * Возвращает объект управления визуалом.
 * @param btnLike
 * @returns {{success: success, startLoading: startLoading, setProgress: setProgress, reset: reset, error: error, btn: (*)}|null}
 */
function createSmartButtonControl(btnLike) {
    const els = normalizeTargets( btnLike );
    if (!els.length) return null;
    const btn = enhanceButton( els[0] ); // используем вашу enhanceButton

    const wave = btn.querySelector('.req-btn__wave');

    function startLoading(indeterminate = false) {
        btn.disabled = true;
        btn.classList.remove('is-success','is-error');
        btn.setAttribute('aria-busy','true');
        if (!wave) return;
        wave.style.width = indeterminate ? '100%' : '0%';
        wave.classList.toggle('req-btn__wave--indet', !!indeterminate);
    }

    function setProgress(p) {
        if (!wave) return;
        wave.classList.remove('req-btn__wave--indet');
        const clamped = Math.max(0, Math.min(100, Number(p) || 0));
        wave.style.width = clamped + '%';
    }

    function success() {
        btn.classList.remove('is-error');
        btn.classList.add('is-success');
    }

    function error() {
        btn.classList.remove('is-success');
        btn.classList.add('is-error');
    }

    function reset(delayMs = 1200) {
        const doReset = () => {
            btn.classList.remove('is-success','is-error');
            if (wave) {
                wave.classList.remove('req-btn__wave--indet');
                wave.style.width = '0%';
            }
            btn.disabled = false;
            btn.setAttribute('aria-busy','false');
        };
        delayMs ? setTimeout(doReset, delayMs) : doReset();
    }

    return { btn, startLoading, setProgress, success, error, reset };
}

/**
 * Скачивание потоково с прогрессом (download) через fetch.
 * Вызывает onProgress(percent|null) где percent 0..100, либо null если total неизвестен.
 * Возвращает объект { response, text()/json() } схожий по использованию.
 */
async function fetchWithDownloadProgress(url, init, onProgress) {
    const response = await fetch(url, init);
    if (!response.body) {
        // без потоков — просто вернуть, прогресс не показать
        return { response, getBody: () => parseByContentType(response) };
    }

    const totalHeader = response.headers.get('Content-Length') || response.headers.get('content-length');
    const total = totalHeader ? parseInt(totalHeader, 10) : null;

    if (!total && typeof onProgress === 'function') onProgress(null);

    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.byteLength ?? value.length ?? 0;
        if (total && typeof onProgress === 'function') {
            const percent = Math.round((received / total) * 100);
            onProgress(percent);
        }
    }

    const merged = new Blob(chunks);

    // создаём «Response» из Blob, чтобы использовать ваши parseByContentType
    const res2 = new Response(merged, { status: response.status, statusText: response.statusText, headers: response.headers });
    res2.url = response.url;
    res2.redirected = response.redirected;

    return {
        response: res2,
        getBody: () => parseByContentType(res2)
    };
}

/**
 * POST через XHR с поддержкой upload/download progress.
 * Возвращает { status, headersObj, responseURL, bodyText }.
 */
function xhrPostWithProgress(url, { headers = {}, body = null, timeoutMs = 15000, signal }, onUpload, onDownload) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.responseType = 'text';
        xhr.timeout = timeoutMs;

        Object.entries(headers || {}).forEach(([k,v]) => xhr.setRequestHeader(k, v));

        xhr.upload && (xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && typeof onUpload === 'function') {
                onUpload(Math.round((e.loaded / e.total) * 100));
            } else if (typeof onUpload === 'function') {
                onUpload(null);
            }
        });

        xhr.onprogress = (e) => {
            if (e.lengthComputable && typeof onDownload === 'function') {
                onDownload(Math.round((e.loaded / e.total) * 100));
            }
        };

        xhr.onload = () => {
            const rawHeaders = xhr.getAllResponseHeaders();
            const headersObj = {};
            rawHeaders.trim().split(/[\r\n]+/).forEach(line => {
                const parts = line.split(': ');
                const header = parts.shift();
                const value = parts.join(': ');
                if (header) headersObj[header.toLowerCase()] = value;
            });
            resolve({
                status: xhr.status,
                headersObj,
                responseURL: xhr.responseURL,
                bodyText: xhr.responseText
            });
        };

        xhr.onerror = () => reject(new Error('XHR network error'));
        xhr.ontimeout = () => reject(new Error('XHR timeout'));
        if (signal) {
            if (signal.aborted) { return reject(new DOMException('Aborted', 'AbortError')); }
            signal.addEventListener('abort', () => xhr.abort());
        }

        xhr.send(body);
    });
}